/* hero3dBg.ts
 *
 * <hero-3d-bg> — A custom WebGL element that renders an interactive
 * 3D grid world with mountains, fog, and a glowing sphere.
 *
 * Fully dependency-free, caps DPR at 1.0 (optimized for GPU performance
 * and silent fans), handles prefers-reduced-motion, and pauses rendering 
 * when scrolled off-screen via IntersectionObserver.
 * 
 * Steers infinitely in 360 degrees using coordinate integration in JS.
 * The camera dynamically climbs/descends to follow the rolling terrain height.
 * The glowing sphere is offset to the right side of the stage, creating a
 * localized gravity well that depresses the grid floor directly underneath it.
 */

const VERT = `
attribute vec2 a_pos;
varying vec2 v_uv;
void main() {
    v_uv = a_pos * 0.5 + 0.5;
    gl_Position = vec4(a_pos, 0.0, 1.0);
}`;

const FRAG = `
#extension GL_OES_standard_derivatives : enable
precision highp float;

uniform vec2  u_res;
uniform float u_time;
uniform vec2  u_mouse;
uniform float u_scroll;
uniform vec2  u_camXZ;
uniform float u_yawAngle;
uniform vec2  u_sphereXZ;

varying vec2 v_uv;

float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
        mix(hash(i),                 hash(i + vec2(1.0, 0.0)), u.x),
        mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
        u.y
    );
}

float evaluateRockNoise(vec2 uv) {
    return noise(uv * 24.0) * 0.4 + noise(uv * 70.0) * 0.3 + noise(uv * 200.0) * 0.3;
}

// 2-octave FBM base terrain height (undepressed)
float getBaseTerrainHeight(vec2 p) {
    float aspect = u_res.x / u_res.y;
    float heightScale = mix(1.25, 1.0, smoothstep(0.4, 1.2, aspect));
    float h = 0.0;
    h += 2.5 * noise(p * 0.16);
    // Subtle, slow terrain drifting for organic breathing motion
    h += 1.1 * noise(p * 0.38 + vec2(4.3, 1.7) + vec2(u_time * 0.05, u_time * 0.03));
    return (h * 1.8 - 2.5) * heightScale;
}

// Procedural craters using fixed 3x3 grid search for smooth C1 continuity
float getCraters(vec2 p) {
    vec2 ip = floor(p);
    vec2 fp = fract(p);
    float h = 0.0;
    
    for (int y = -1; y <= 1; y++) {
        for (int x = -1; x <= 1; x++) {
            vec2 cell = vec2(float(x), float(y));
            vec2 id = ip + cell;
            float h1 = hash(id);
            if (h1 > 0.4) {
                float h2 = hash(id + vec2(13.4, 5.7));
                float h3 = hash(id + vec2(9.1, 17.3));
                
                // Allow center to wander slightly within/outside the cell
                vec2 center = cell + vec2(h1, h2) * 0.7 + 0.15;
                float radius = 0.25 + 0.35 * h3;
                float depth = 0.5 + 0.5 * hash(id - vec2(7.2, 11.8));
                
                float d = length(fp - center) / radius;
                float bowl = -0.55 * smoothstep(1.15, 0.0, d);
                float rim = 0.22 * smoothstep(0.4, 0.0, abs(d - 1.0));
                h += (bowl + rim) * depth;
            }
        }
    }
    return h;
}

float getTerrainHeight(vec2 p) {
    float aspect = u_res.x / u_res.y;
    float heightScale = mix(1.25, 1.0, smoothstep(0.4, 1.2, aspect));
    float baseH = getBaseTerrainHeight(p);
    
    // Blend in procedural moon craters on the right side of the view
    vec2 camRight = vec2(cos(u_yawAngle), -sin(u_yawAngle));
    float localX = dot(p - u_camXZ, camRight);
    float transitionStart = mix(-3.5, -2.0, smoothstep(0.4, 1.2, aspect));
    float transitionEnd = mix(1.5, 3.5, smoothstep(0.4, 1.2, aspect));
    float rightSide = smoothstep(transitionStart, transitionEnd, localX);
    
    if (rightSide > 0.001) {
        float primary = getCraters(p * 0.22) * 1.6;
        float secondary = getCraters(p * 0.57 + vec2(5.2, 9.1)) * 0.55;
        baseH += (primary + secondary) * rightSide * heightScale;
    }
    
    return baseH;
}

float getSDF(vec3 p, out int objId) {
    objId = 0;
    return (p.y - getTerrainHeight(p.xz)) * 0.8;
}

vec3 getTerrainNormal(vec3 p) {
    vec2 eps = vec2(0.01, 0.0);
    vec3 N = normalize(vec3(
        getTerrainHeight(p.xz - eps.xy) - getTerrainHeight(p.xz + eps.xy),
        2.0 * eps.x,
        getTerrainHeight(p.xz - eps.yx) - getTerrainHeight(p.xz + eps.yx)
    ));
    return N;
}

void main() {
    vec2 uv = v_uv * 2.0 - 1.0;
    float aspect = u_res.x / u_res.y;
    uv.x *= aspect;
    
    // Camera Y clearance-rides the base terrain (for flight stability)
    float camY = getBaseTerrainHeight(u_camXZ) + 1.45;
    vec3 ro = vec3(u_camXZ.x, camY, u_camXZ.y);
    
    // Pitch/yaw: combine JS heading angle + mouse look offset
    float yaw = u_yawAngle + u_mouse.x * 0.15;
    // Look slightly more downward on mobile to pull the landscape higher up on the screen
    float pitchOffset = mix(-0.14, 0.0, smoothstep(0.4, 1.2, aspect));
    float pitch = u_mouse.y * 0.10 - (u_scroll - 0.5) * 0.8 + pitchOffset;
    
    vec3 forward = vec3(sin(yaw), sin(pitch), cos(yaw) * cos(pitch));
    vec3 right = normalize(cross(vec3(0.0, 1.0, 0.0), forward));
    vec3 up = cross(forward, right);
    
    // Zoom out (increase FOV) on portrait viewports to frame both sides of the landscape
    float fovScale = mix(1.6, 1.0, smoothstep(0.4, 1.2, aspect));
    vec3 rd = normalize(forward + uv.x * fovScale * right + uv.y * fovScale * up);
    
    // Color Palette
    vec3 cCream = vec3(0.910, 0.902, 0.843); // #e8e6d7
    vec3 cRed   = vec3(0.682, 0.169, 0.118); // #ae2b1e
    vec3 cOx    = vec3(0.380, 0.090, 0.063); // #611710 (Oxblood)
    vec3 cBlack = vec3(0.047, 0.047, 0.047); // #0c0c0c
    vec3 cGrey  = vec3(0.235, 0.235, 0.235); // #3c3c3c
    
    float t = 0.0;
    int objId = -1;
    float sphereGlow = 0.0;
    float prev_t = 0.0;
    float prev_d = 0.0;
    
    // GPU Optimization: Reduced steps to 52 for silent fans and power saving
    for (int i = 0; i < 52; i++) {
        vec3 p = ro + rd * t;
        int curId;
        float d = getSDF(p, curId);
        
        if (d < 0.001) {
            if (i > 0) {
                float ta = prev_t;
                float tb = t;
                for (int r = 0; r < 5; r++) {
                    float tc = mix(ta, tb, 0.5);
                    int dummyId;
                    float dc = getSDF(ro + rd * tc, dummyId);
                    if (dc < 0.0) {
                        tb = tc;
                    } else {
                        ta = tc;
                    }
                }
                t = tb;
            }
            objId = curId;
            break;
        }
        if (t > 64.0) break;
        prev_t = t;
        prev_d = d;
        t += d * 0.8 + 0.16;
    }
    
    vec3 col = cCream;
    
    if (objId == 0) {
        vec3 p = ro + rd * t;
        vec3 N = getTerrainNormal(p);
        
        // Simple directional lighting
        vec3 L = normalize(vec3(0.5, 2.0, 1.0));
        float diff = max(0.0, dot(N, L));
        
        vec3 terrainBase = mix(cCream, cGrey, 0.06);
        vec3 terrainShaded = mix(terrainBase, cBlack, (1.0 - diff) * 0.18);
        
        // Rocky Moon surface texture (only on the right side of the view)
        vec2 camRight = vec2(cos(u_yawAngle), -sin(u_yawAngle));
        float localX = dot(p.xz - u_camXZ, camRight);
        float transitionStart = mix(-3.5, -2.0, smoothstep(0.4, 1.2, aspect));
        float transitionEnd = mix(1.5, 3.5, smoothstep(0.4, 1.2, aspect));
        float rightSide = smoothstep(transitionStart, transitionEnd, localX);
        vec3 finalTerrainBase = terrainShaded;
        
        if (rightSide > 0.001) {
            // Biplanar mapping to eliminate texture stretching on steep crater walls
            vec3 blend = abs(N);
            blend = pow(blend, vec3(4.0)); // Sharpen transition to dominant plane
            blend /= (blend.x + blend.y + blend.z);
            
            float noiseY = evaluateRockNoise(p.xz);
            float noiseSide = 0.0;
            if (blend.x > blend.z) {
                noiseSide = evaluateRockNoise(p.zy);
            } else {
                noiseSide = evaluateRockNoise(p.xy);
            }
            float rockNoise = mix(noiseSide, noiseY, blend.y);
            
            // Ultra-fine sand grains (regolith) close to camera
            float closeRock = smoothstep(12.0, 3.0, t);
            if (closeRock > 0.0) {
                float microRegolith = noise(p.xz * 600.0);
                rockNoise = mix(rockNoise, rockNoise * 0.6 + microRegolith * 0.4, closeRock);
            }
            
            // Multi-scale color patches (maria vs highlands)
            float patches = noise(p.xz * 0.15) * 0.6 + noise(p.xz * 0.6) * 0.4;
            
            // Warm-grey moon palette matching the site's warm cream design
            vec3 cMoonDark  = vec3(0.18, 0.17, 0.16); // Warm charcoal maria
            vec3 cMoonLight = vec3(0.55, 0.53, 0.50); // Light highlands
            vec3 cMoonDust  = vec3(0.38, 0.36, 0.34); // Regolith grey
            
            vec3 moonBase = mix(cMoonDark, cMoonLight, patches * 0.4 + rockNoise * 0.6);
            
            // Highlight crater rims with dusty ejecta blanket coloring
            float primCraters = getCraters(p.xz * 0.22);
            float rimHighlight = smoothstep(0.02, 0.2, primCraters);
            moonBase = mix(moonBase, cMoonLight * 1.25, rimHighlight * 0.35);
            
            // Stark shadow lighting for the vacuum of space (8% ambient fill for readability)
            float moonDiff = max(0.0, dot(N, L));
            vec3 moonShaded = mix(moonBase * 0.08, moonBase, moonDiff * 0.92 + 0.08);
            
            // Blend landscape from grid-cream on left to moon-grey on right
            finalTerrainBase = mix(terrainShaded, moonShaded, rightSide);
        }
        
        // Screen-space derivative grid lines (perfectly crisp constant pixel width)
        vec2 gridCoords = p.xz * 5.0; // Tight grid pattern wrapping the topography
        vec2 gridFract = abs(fract(gridCoords - 0.5) - 0.5);
        vec2 dxy = fwidth(gridCoords);
        vec2 line = smoothstep(dxy * 0.75, dxy * 1.75, gridFract);
        float gridVal = 1.0 - min(line.x, line.y);
        
        vec3 gridColor = mix(cRed, cOx, 0.2);
        // Dynamic grid energy wave animation (sweeping pulse)
        float gridPulse = sin(p.x * 0.25 - p.z * 0.15 - u_time * 0.8) * 0.5 + 0.5;
        gridColor = mix(gridColor, cRed * 1.4, gridPulse * 0.35);
        
        // Fade the grid line intensity in the far distance to avoid layout noise
        float fade = smoothstep(64.0, 20.0, t);
        // Blend out the grid on the right side (where moon surface is located)
        float gridSideFade = 1.0 - rightSide;
        vec3 terrainCol = mix(finalTerrainBase, gridColor, gridVal * 0.28 * fade * gridSideFade);
        
        float fog = exp(-pow(t * 0.022, 4.0));
        col = mix(cCream, terrainCol, fog);
    }
    
    gl_FragColor = vec4(col, 1.0);
}`;

export class Hero3dBg extends HTMLElement {
    private canvas!: HTMLCanvasElement;
    private gl: WebGLRenderingContext | null = null;
    private uniforms: Record<string, WebGLUniformLocation | null> = {};

    private rafId = 0;
    private visible = false;
    private startTime = performance.now();
    private progress = 0.5;

    // Mouse variables (target and current smoothed)
    private mouseX = 0;
    private mouseY = 0;
    private targetMouseX = 0;
    private targetMouseY = 0;

    private reducedMotion = false;
    private observer: IntersectionObserver | null = null;
    private resizer: ResizeObserver | null = null;

    // Movement integration properties (free 360-degree navigation)
    private lastTime = performance.now();
    private yawAngle = 0.0;
    private camX = 0.0;
    private camZ = 0.0;

    connectedCallback() {
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.canvas = document.createElement('canvas');
        this.appendChild(this.canvas);

        this.gl = this.canvas.getContext('webgl', {
            alpha: false,
            antialias: false,
            premultipliedAlpha: false,
        });

        if (!this.gl) return; // Fallback to background color

        this.compile();
        this.fit();

        this.resizer = new ResizeObserver(() => {
            this.fit();
            this.renderFrame();
        });
        this.resizer.observe(this);

        this.observer = new IntersectionObserver(([entry]) => {
            this.visible = !!entry?.isIntersecting;
            if (this.visible) this.play();
            else this.pause();
        });
        this.observer.observe(this);

        // Bind mouse and touch events
        window.addEventListener('mousemove', this.onMouseMove);
        window.addEventListener('touchmove', this.onTouchMove, { passive: true });
    }

    disconnectedCallback() {
        this.pause();
        this.observer?.disconnect();
        this.resizer?.disconnect();
        window.removeEventListener('mousemove', this.onMouseMove);
        window.removeEventListener('touchmove', this.onTouchMove);
    }

    setProgress(p: number) {
        this.progress = p;
        if (this.reducedMotion && this.visible) this.renderFrame();
    }

    private onMouseMove = (e: MouseEvent) => {
        this.targetMouseX = (e.clientX / window.innerWidth) * 2 - 1;
        this.targetMouseY = -(e.clientY / window.innerHeight) * 2 + 1;

        // If reduced motion, we don't have a loop running, but still want responsive mouse updates
        if (this.reducedMotion && this.visible) {
            this.mouseX = this.targetMouseX;
            this.mouseY = this.targetMouseY;
            this.renderFrame();
        }
    };

    private onTouchMove = (e: TouchEvent) => {
        if (e.touches.length === 0) return;
        const touch = e.touches[0];
        this.targetMouseX = (touch.clientX / window.innerWidth) * 2 - 1;
        this.targetMouseY = -(touch.clientY / window.innerHeight) * 2 + 1;

        if (this.reducedMotion && this.visible) {
            this.mouseX = this.targetMouseX;
            this.mouseY = this.targetMouseY;
            this.renderFrame();
        }
    };

    private play() {
        this.lastTime = performance.now();
        if (this.rafId || this.reducedMotion) {
            this.renderFrame();
            if (this.reducedMotion) return;
        }
        const loop = () => {
            const now = performance.now();
            const dt = Math.min(0.1, (now - this.lastTime) / 1000);
            this.lastTime = now;

            // Apply physics-based smooth interpolation for mouse coordinate shifts
            this.mouseX += (this.targetMouseX - this.mouseX) * 0.07;
            this.mouseY += (this.targetMouseY - this.mouseY) * 0.07;

            // Heading yaw angle is driven directly by the incoming scroll-pin progress (0.0 -> 1.0)
            const targetYaw = this.progress * -1.8;
            this.yawAngle += (targetYaw - this.yawAngle) * 0.08;

            // Move camera forward slowly (0.225 speed, slowed down by 50% for an ultra-dreamy, premium feel)
            this.camX += Math.sin(this.yawAngle) * 0.225 * dt;
            this.camZ += Math.cos(this.yawAngle) * 0.225 * dt;

            this.renderFrame();
            this.rafId = requestAnimationFrame(loop);
        };
        this.rafId = requestAnimationFrame(loop);
    }

    private pause() {
        if (this.rafId) cancelAnimationFrame(this.rafId);
        this.rafId = 0;
    }

    private compile() {
        const gl = this.gl!;
        gl.getExtension('OES_standard_derivatives');

        const make = (type: number, src: string) => {
            const s = gl.createShader(type)!;
            gl.shaderSource(s, src);
            gl.compileShader(s);
            if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
                console.error(gl.getShaderInfoLog(s));
            }
            return s;
        };

        const prog = gl.createProgram()!;
        gl.attachShader(prog, make(gl.VERTEX_SHADER, VERT));
        gl.attachShader(prog, make(gl.FRAGMENT_SHADER, FRAG));
        gl.linkProgram(prog);

        if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
            console.error(gl.getProgramInfoLog(prog));
        }
        gl.useProgram(prog);

        const quad = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, quad);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);

        const loc = gl.getAttribLocation(prog, 'a_pos');
        gl.enableVertexAttribArray(loc);
        gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

        for (const name of ['u_res', 'u_time', 'u_mouse', 'u_scroll', 'u_camXZ', 'u_yawAngle', 'u_sphereXZ']) {
            this.uniforms[name] = gl.getUniformLocation(prog, name);
        }
    }

    private fit() {
        // GPU Optimization: Cap DPR at 1.0 (down from 1.5) to render fewer pixels and run silent/cool
        const dpr = Math.min(window.devicePixelRatio || 1, 1.0);
        const w = Math.max(1, Math.round(this.clientWidth * dpr));
        const h = Math.max(1, Math.round(this.clientHeight * dpr));
        if (this.canvas.width === w && this.canvas.height === h) return;

        this.canvas.width = w;
        this.canvas.height = h;
        this.gl?.viewport(0, 0, w, h);
    }

    private renderFrame() {
        const gl = this.gl;
        if (!gl) return;

        // Frozen/static mode for reduced motion
        const timeVal = this.reducedMotion ? 5.0 : (performance.now() - this.startTime) / 1000;

        const currentCamZ = this.reducedMotion ? 0.0 : this.camZ;
        const currentCamX = this.reducedMotion ? 0.0 : this.camX;
        const currentYaw = this.reducedMotion ? 0.0 : this.yawAngle;

        gl.uniform2f(this.uniforms.u_res, this.canvas.width, this.canvas.height);
        gl.uniform1f(this.uniforms.u_time, timeVal);
        gl.uniform2f(this.uniforms.u_mouse, this.mouseX, this.mouseY);
        gl.uniform1f(this.uniforms.u_scroll, this.progress);

        // Pass camera position X and Z
        gl.uniform2f(this.uniforms.u_camXZ, currentCamX, currentCamZ);

        // Pass yaw heading angle
        gl.uniform1f(this.uniforms.u_yawAngle, currentYaw);

        // Pass sphere XZ coordinates floating 3.6 units ahead and offset to the right side of the screen.
        // On widescreen, scale the right offset by the aspect ratio so it stays on the right side.
        const aspect = this.canvas.width / this.canvas.height;
        const baseRightOffset = Math.max(1.2, aspect * 1.65);
        const rightOffset = baseRightOffset + this.mouseX * 0.6;
        const sphereX = currentCamX + Math.sin(currentYaw) * 3.6 + Math.cos(currentYaw) * rightOffset;
        const sphereZ = currentCamZ + Math.cos(currentYaw) * 3.6 - Math.sin(currentYaw) * rightOffset;
        gl.uniform2f(this.uniforms.u_sphereXZ, sphereX, sphereZ);

        gl.clearColor(0.910, 0.902, 0.843, 1.0); // #e8e6d7 (cream clear)
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
}

customElements.define('hero-3d-bg', Hero3dBg);
